import { SCRIPTED_ID_BASE } from '@troyai/portal/investor-demo/core';

/**
 * Every fixed id in the demo story. The fixtures, the interceptor and the store all read from here
 * so an id is never typed twice. All of them sit in the scripted range (`SCRIPTED_ID_BASE` and up),
 * which real data never reaches: each figure below is `SCRIPTED_ID_BASE +` the number in the
 * comment. New objects the demo creates take ids from `DemoData.nextId` (from
 * `SCRIPTED_ID_BASE + 990000`), which stays clear of every range below.
 */

/**
 * The four fixture episodes (9001 to 9004). The pharmacy story's four take the same ids
 * (`PHARMACY_EPISODE_IDS` in demo-core, for the pages): only one story is seeded at a time.
 */
export const EPISODE_IDS = {
  chf: SCRIPTED_ID_BASE + 9001,
  sdoh: SCRIPTED_ID_BASE + 9002,
  eyeExam: SCRIPTED_ID_BASE + 9003,
  pneumonia: SCRIPTED_ID_BASE + 9004,
} as const;

/** The HRA assessment submitted Sep 24 (9101). */
export const ASSESSMENT_ID = SCRIPTED_ID_BASE + 9101;

/**
 * Care plan templates 9151 to 9162, one per recommendation row, each with three barrier, goal and
 * intervention entries (ids 9251 to 9286, counted by `templateMetaId(row, 1 | 2 | 3)`).
 */
export const templateId = (row: number): number => SCRIPTED_ID_BASE + 9150 + row;
export const templateMetaId = (row: number, n: 1 | 2 | 3): number =>
  SCRIPTED_ID_BASE + 9250 + (row - 1) * 3 + n;

/** Care plan suggestions 9201 to 9212; `suggestionId(1)` is the first row of the spec's table. */
export const suggestionId = (row: number): number => SCRIPTED_ID_BASE + 9200 + row;

/** The two actions: the fax to Dr. Patel (9601) and the ride benefit set-up (9602). */
export const ACTION_IDS = {
  faxToPatel: SCRIPTED_ID_BASE + 9601,
  rideBenefit: SCRIPTED_ID_BASE + 9602,
} as const;

/** Member outreach 9501 to 9503; `outreachId(1)` is the first. */
export const outreachId = (n: number): number => SCRIPTED_ID_BASE + 9500 + n;

/**
 * Targets count up from 9300, proposals from 9400, episode events from 9800 (20 per episode, so
 * 9821 to 9899) and recommendation lifecycle events from 9930 (4 per episode, so 9935 to 9949).
 */
export const TARGET_ID_BASE = SCRIPTED_ID_BASE + 9300;
export const PROPOSAL_ID_BASE = SCRIPTED_ID_BASE + 9400;
export const EPISODE_EVENT_ID_BASE = SCRIPTED_ID_BASE + 9800;
export const RECOMMENDATION_EVENT_ID_BASE = SCRIPTED_ID_BASE + 9930;

/** Recommendations 9901 to 9912; `recommendationId(1)` is the first row of the spec's table. */
export const recommendationId = (row: number): number => SCRIPTED_ID_BASE + 9900 + row;

/** Clinical events (`clinical_event.id`) count up from 9700: 9700 to 9799 are reserved. */
export const CLINICAL_EVENT_ID_BASE = SCRIPTED_ID_BASE + 9700;

/** The first id handed out to anything the demo creates while it runs (990000). */
export const FIRST_NEW_ID = SCRIPTED_ID_BASE + 990000;
